/**
 * Reusable Variant Selector Module
 * Handles variant selection for both dropdown and button style selectors
 */
window.VariantSelector = (function() {
  
  // Private variables
  const instances = {};
  
  // Private methods
  function getVariantsForForm(formId) {
    const variantsElement = document.getElementById(`product-variants-${formId}`);
    if (!variantsElement) return [];
    
    try {
      return JSON.parse(variantsElement.textContent);
    } catch (error) {
      console.error('Error parsing product variants:', error);
      return [];
    }
  }
  
  function updateVariantId(formId) {
    const instance = instances[formId];
    if (!instance) return;
    const variants = getVariantsForForm(formId);
    if (!variants.length) return;
    const names = JSON.parse(document.getElementById(`product-options-${formId}`)?.textContent || '[]');
    const controls = [...document.querySelectorAll(`[data-form-id="${formId}"].variant-buttons, [data-form-id="${formId}"].variant-selector`)];
    const selections = names.map((name, index) => {
      const control = controls.find(c => Number(c.dataset.optionPosition) === index + 1);
      if (control) return control.matches('select') ? control.value : control.querySelector('.selected')?.dataset.optionValue;
      const values = [...new Set(variants.map(v => v.options[index]))];
      return values.length === 1 ? values[0] : undefined;
    });
    // Match positions, never merely a value appearing somewhere in a variant.
    const matchingVariant = variants.find(v => v.options.every((value, index) => selections[index] === value));
    instance.selectedVariantId = matchingVariant?.id || null;
    const currentSelections = Object.fromEntries(names.map((name, i) => [name, selections[i]]));
    controls.forEach(control => {
      const index = Number(control.dataset.optionPosition) - 1;
      const isColor = control.dataset.isColor === 'true';
      control.querySelectorAll('.variant-button, option[data-option-value]').forEach(choice => {
        const candidates = variants.filter(v => v.options[index] === choice.dataset.optionValue &&
          (isColor || selections.every((value, i) => i === index || !value || v.options[i] === value)));
        const available = candidates.some(v => v.available);
        choice.dataset.available = String(available);
        if (choice.tagName === 'BUTTON') {
          choice.disabled = !candidates.length;
          choice.classList.toggle('line-through', !available && !isColor);
          choice.classList.toggle('opacity-30', !available && !isColor);
          choice.setAttribute('aria-pressed', String(choice.classList.contains('selected')));
        } else choice.disabled = !candidates.length;
      });
      if (isColor) {
        const label = control.closest('.variant-option')?.querySelector('[data-native-color-label]');
        if (label) label.textContent = selections[index] || '';
      }
    });
    const hidden = document.querySelector(`[data-form-id="${formId}"].variant-id-input`);
    if (hidden) hidden.value = matchingVariant?.id || '';
    if (formId === 'main-product') {
      const input = document.querySelector('#add-to-cart-form [name="id"]');
      if (input) input.value = matchingVariant?.id || '';
      const add = document.getElementById('add-to-cart-btn');
      const sold = document.getElementById('sold-out-btn');
      const notify = document.getElementById('notify-me-btn');
      if (add) {
        if (!instance.addLabel) instance.addLabel = add.textContent.trim();
        add.style.display = matchingVariant && !matchingVariant.available ? 'none' : '';
        add.disabled = false;
        add.textContent = instance.addLabel;
        add.style.backgroundColor = '';
        add.style.color = '';
        add.classList.remove('button--disabled');
      }
      if (sold) sold.style.display = matchingVariant && !matchingVariant.available ? '' : 'none';
      if (notify) notify.style.display = matchingVariant && !matchingVariant.available ? '' : 'none';
      const colorIndex = controls.find(c => c.dataset.isColor === 'true')?.dataset.optionPosition;
      const imageVariant = matchingVariant || (colorIndex && variants.find(v => v.options[Number(colorIndex)-1] === selections[Number(colorIndex)-1]));
      const mediaId = imageVariant?.featured_media?.id || imageVariant?.featured_image?.id;
      if (mediaId && instance.mediaId !== mediaId) {
        instance.mediaId = mediaId;
        setTimeout(() => {
          const slides = [...document.querySelectorAll('#main-splide .splide__slide:not(.splide__slide--clone)')];
          const path = value => String(value || '').split('?')[0].split('/').pop();
          const index = slides.findIndex(slide => slide.dataset.mediaId === String(mediaId) || (imageVariant.featured_image?.src && path(slide.dataset.mediaSrc) === path(imageVariant.featured_image.src)));
          if (index >= 0 && window.productSplide) {
            const speed = window.productSplide.options.speed;
            window.productSplide.options = { speed: 0 };
            window.productSplide.go(index);
            window.productSplide.options = { speed };
          }
          if (index >= 0 && window.thumbnailSplide) window.thumbnailSplide.go(index);
        }, 0);
      }
      // Preserve preview parameters and choose only a fully specified variant.
      const url = new URL(window.location.href);
      if (matchingVariant) url.searchParams.set('variant', matchingVariant.id);
      else url.searchParams.delete('variant');
      window.history.replaceState(window.history.state, '', url);
    }
    document.dispatchEvent(new CustomEvent('variantChanged', {
      detail: { formId, variant: matchingVariant || null, selections: currentSelections }
    }));
  }

  function initializeDropdown(formId) {
    const selectors = document.querySelectorAll(`[data-form-id="${formId}"].variant-selector`);
    
    selectors.forEach(selector => {
      // Add change event listener
      selector.addEventListener('change', () => updateVariantId(formId));
    });
  }
  
  function initializeButtons(formId) {
    const buttonContainers = document.querySelectorAll(`[data-form-id="${formId}"].variant-buttons`);
    
    buttonContainers.forEach(container => {
      const buttons = container.querySelectorAll('.variant-button');
      
      buttons.forEach(button => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          
          if (button.disabled) return;
          
          // Remove selected class from siblings
          buttons.forEach(btn => btn.classList.remove('selected'));
          
          // Add selected class to clicked button
          button.classList.add('selected');
          
          updateVariantId(formId);
        });
      });
    });
  }
  
  // Public API
  return {
    init: function(formId, selectorType = 'dropdown') {
      // Store instance configuration
      instances[formId] = {
        selectorType: selectorType,
        selectedVariantId: null
      };

      if (selectorType === 'dropdown') {
        initializeDropdown(formId);
      } else if (selectorType === 'buttons') {
        initializeButtons(formId);
      }

      const variants = getVariantsForForm(formId);
      const requested = formId === 'main-product' ? new URL(window.location.href).searchParams.get('variant') : null;
      const initial = variants.find(v => String(v.id) === requested);
      document.querySelectorAll(`[data-form-id="${formId}"].variant-buttons, [data-form-id="${formId}"].variant-selector`).forEach(control => {
        const index = Number(control.dataset.optionPosition) - 1;
        const values = [...new Set(variants.map(v => v.options[index]))];
        const value = initial?.options[index] || (control.dataset.isColor === 'true' || values.length === 1 ? values[0] : null);
        if (!value) return;
        if (control.matches('select')) control.value = value;
        else control.querySelectorAll('.variant-button').forEach(button => button.classList.toggle('selected', button.dataset.optionValue === value));
      });

      // Initialize variant ID — also handles single-variant products with no selector buttons
      updateVariantId(formId);

      // Single-variant fallback: if no variant matched via selectors (no buttons/dropdowns),
      // read the id input directly and apply availability state
      if (formId === 'main-product') {
        const idInput = document.querySelector('#add-to-cart-form [name="id"]');
        const variantsElement = document.getElementById(`product-variants-${formId}`);
        if (idInput && variantsElement && !instances[formId].selectedVariantId) {
          try {
            const variants = JSON.parse(variantsElement.textContent);
            const singleVariant = variants.find(v => String(v.id) === String(idInput.value));
            if (singleVariant) {
              instances[formId].selectedVariantId = singleVariant.id;
              const addToCartBtn = document.getElementById('add-to-cart-btn');
              const soldOutBtn = document.getElementById('sold-out-btn');
              const notifyMeBtn = document.getElementById('notify-me-btn');
              if (!singleVariant.available) {
                if (addToCartBtn) addToCartBtn.style.display = 'none';
                if (soldOutBtn) soldOutBtn.style.display = '';
                if (notifyMeBtn) notifyMeBtn.style.display = '';
              }
            }
          } catch (e) {}
        }
      }
    },
    
    getSelectedVariantId: function(formId) {
      const instance = instances[formId];
      return instance ? instance.selectedVariantId : null;
    },
    
    addToCart: function(formId, variantId = null, quantity = 1) {
      const selectedVariantId = variantId || this.getSelectedVariantId(formId);
      
      if (!selectedVariantId) {
        console.error('No variant selected for add to cart');
        return Promise.reject('No variant selected');
      }
      
      return fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: selectedVariantId,
          quantity: quantity
        })
      })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || 'Cart request failed');
        return data;
      })
      .then(data => {
        console.log('Product added to cart:', data);
        
        // Update cart contents
        return fetch((window.Shopify?.routes?.root || '/') + 'cart.js');
      })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || 'Cart request failed');
        return data;
      })
      .then(async cart => {
        // Use centralized cart update function for consistency
        if (window.updateCart) {
          console.log('🔄 VariantSelector using centralized updateCart');
          await window.updateCart(cart);
        } else {
          // Fallback to individual functions if updateCart is not available
          console.log('⚠️ VariantSelector using fallback cart update');
          if (window.updateMenuCart) window.updateMenuCart(cart);
          if (window.updateMainCart) window.updateMainCart(cart);
          if (window.updateCartCount) window.updateCartCount(cart.item_count);
        }
        
        // Open cart drawer if function exists
        if (window.toggleCartDrawer) window.toggleCartDrawer();
        
        return cart;
      });
    }
  };
})(); 
